import dayjs from 'dayjs';
import React, { JSX, useRef } from 'react';
import { Response, ServiceId } from '@/@types/application';
import { PostType } from '@/@types/post';
import useRequest from '../libs/useRequest';
import Status from './Status';
import StatusSkeleton from './StatusSkeleton';

type Props = {
  serviceId: ServiceId;
  location: string;
};

const StatusPanel: React.FC<Props> = ({ serviceId, location }): JSX.Element | null => {
  const timeRange = 12;
  const now = useRef(dayjs());
  const endPeriod = useRef(dayjs().add(timeRange, 'hours'));

  const { data, error } = useRequest<Response<PostType>, Error>(
    serviceId
      ? {
          url: 'feeds/',
          params: {
            service: serviceId,
            type: 'maint,trouble',
            ordering: '-event_start',
            limit: 200,
          },
        }
      : null,
  );

  if (!data) {
    return <StatusSkeleton serviceId={serviceId} status="loading" location={location} />;
  }

  if (error) {
    return <StatusSkeleton serviceId={serviceId} status="error" location={location} />;
  }

  if (data) {
    const currentEvents = data.results.filter(
      (post) => +post.event_end > now.current.unix() && +post.event_start < endPeriod.current.unix(),
    );
    const troubleNum = currentEvents.filter((post) => post.type === 'trouble').length;
    const maintNum = currentEvents.filter((post) => post.type === 'maint').length;

    const toServiceStatusCode = (troubleNum: number, maintNum: number): string => {
      if ((troubleNum && maintNum) || troubleNum) return 'trouble';
      else if (maintNum) return 'maint';
      else return 'normal';
    };

    const toServiceStatusName = (serviceStatusCode: string): string => {
      if (serviceStatusCode === 'trouble') return '一部 障害発生中';
      else if (serviceStatusCode === 'maint') return '一部 メンテナンス予定あり';
      else return 'メンテナンス・障害なし';
    };

    const makeServiceStatusMessage = (troubleNum: number, maintNum: number) => {
      if (troubleNum && maintNum) return `障害 ${troubleNum}件・メンテナンス予定 ${maintNum}件`;
      else if (troubleNum) return `障害 ${troubleNum}件`;
      else if (maintNum) return `メンテナンス予定 ${maintNum}件`;
      else return 'メンテナンス・障害はありません';
    };

    const serviceStatusCode = toServiceStatusCode(troubleNum, maintNum);
    const serviceStatusName = toServiceStatusName(serviceStatusCode);
    const serviceStatusMessage = makeServiceStatusMessage(troubleNum, maintNum);

    return (
      <Status
        serviceId={serviceId}
        serviceStatusCode={serviceStatusCode}
        serviceStatusName={serviceStatusName}
        serviceStatusMessage={serviceStatusMessage}
        location={location}
      />
    );
  }

  return null;
};

export default StatusPanel;
